import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, getDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { User, Users, Search, Eye, Edit, Mail, ArrowLeft, Shield, Calendar, Building2, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import Swal from 'sweetalert2';

interface UnitMembership {
  unitId: string;
  role: string;
  joinedAt: Date;
}

interface GuestSubscription {
  unitId: string;
  subscribedAt: Date;
}

interface UserData {
  uid: string;
  email: string;
  name: string;
  createdAt: Date;
  lastLoginAt: Date;
  emailVerified: boolean;
  isAdmin: boolean;
  profileComplete: boolean;
  twoFactorEnabled: boolean;
  units: UnitMembership[];
  guestSubscriptions?: GuestSubscription[];
  rank?: string;
  unitName?: string;
  unitNumber?: string;
  motherUnitName?: string;
  motherUnitNumber?: string;
}

interface UnitData {
  id: string;
  name: string;
  number: string;
  type: string;
  role?: string;
  joinedAt?: Date;
}

export default function UserManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [userUnits, setUserUnits] = useState<UnitData[]>([]);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UserData>>({});
  const [sendingReset, setSendingReset] = useState(false);

  useEffect(() => {
    fetchRecentUsers();
  }, []);

  const fetchRecentUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'), limit(10));
      const snapshot = await getDocs(q);
      
      const usersData: UserData[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        usersData.push({
          uid: doc.id,
          email: data.email || '',
          name: data.name || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
          emailVerified: data.emailVerified || false,
          isAdmin: data.isAdmin || false,
          profileComplete: data.profileComplete || false,
          twoFactorEnabled: data.twoFactorEnabled || false,
          units: (data.units || []).map((u: any) => ({
            unitId: u.unitId,
            role: u.role || 'Member',
            joinedAt: u.joinedAt?.toDate() || new Date(),
          })),
          guestSubscriptions: (data.guestSubscriptions || []).map((g: any) => ({
            unitId: g.unitId,
            subscribedAt: g.subscribedAt?.toDate() || new Date(),
          })),
          rank: data.rank,
          unitName: data.unitName,
          unitNumber: data.unitNumber,
          motherUnitName: data.motherUnitName,
          motherUnitNumber: data.motherUnitNumber,
        });
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load users',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const results: UserData[] = [];
      const query = searchQuery.toLowerCase();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const email = (data.email || '').toLowerCase();
        const name = (data.name || '').toLowerCase();
        
        if (email.includes(query) || name.includes(query)) {
          results.push({
            uid: doc.id,
            email: data.email || '',
            name: data.name || '',
            createdAt: data.createdAt?.toDate() || new Date(),
            lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
            emailVerified: data.emailVerified || false,
            isAdmin: data.isAdmin || false,
            profileComplete: data.profileComplete || false,
            twoFactorEnabled: data.twoFactorEnabled || false,
            units: (data.units || []).map((u: any) => ({
              unitId: u.unitId,
              role: u.role || 'Member',
              joinedAt: u.joinedAt?.toDate() || new Date(),
            })),
            guestSubscriptions: (data.guestSubscriptions || []).map((g: any) => ({
              unitId: g.unitId,
              subscribedAt: g.subscribedAt?.toDate() || new Date(),
            })),
            rank: data.rank,
            unitName: data.unitName,
            unitNumber: data.unitNumber,
            motherUnitName: data.motherUnitName,
            motherUnitNumber: data.motherUnitNumber,
          });
        }
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to search users',
      });
    } finally {
      setSearching(false);
    }
  };

  const handleViewUser = async (user: UserData) => {
    setSelectedUser(user);
    
    // Fetch user's unit memberships
    const unitsList: UnitData[] = [];
    for (const membership of user.units) {
      try {
        const unitDoc = await getDoc(doc(db, 'units', membership.unitId));
        if (unitDoc.exists()) {
          const unitData = unitDoc.data();
          unitsList.push({
            id: unitDoc.id,
            name: unitData.name || '',
            number: unitData.unitNumber || unitData.number || '',
            type: unitData.type || 'Unknown',
            role: membership.role,
            joinedAt: membership.joinedAt,
          } as any);
        }
      } catch (error) {
        console.error('Error fetching unit:', error);
      }
    }
    
    // Fetch guest subscription units
    if (user.guestSubscriptions) {
      for (const subscription of user.guestSubscriptions) {
        try {
          const unitDoc = await getDoc(doc(db, 'units', subscription.unitId));
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            // Check if not already in memberships
            if (!unitsList.find(u => u.id === unitDoc.id)) {
              unitsList.push({
                id: unitDoc.id,
                name: unitData.name || '',
                number: unitData.unitNumber || unitData.number || '',
                type: unitData.type || 'Unknown',
                role: 'Guest Subscription',
                joinedAt: subscription.subscribedAt,
              } as any);
            }
          }
        } catch (error) {
          console.error('Error fetching guest subscription unit:', error);
        }
      }
    }
    
    setUserUnits(unitsList);
    setIsViewDialogOpen(true);
  };

  const handleEditUser = (user: UserData) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      rank: user.rank,
      isAdmin: user.isAdmin,
      emailVerified: user.emailVerified,
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        name: editForm.name,
        rank: editForm.rank || null,
        isAdmin: editForm.isAdmin || false,
        emailVerified: editForm.emailVerified || false,
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'User updated successfully',
        timer: 2000,
        showConfirmButton: false,
      });

      setIsEditDialogOpen(false);
      fetchRecentUsers();
      if (searchQuery) {
        handleSearch();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update user',
      });
    }
  };

  const handleSendPasswordReset = async (userEmail: string) => {
    const result = await Swal.fire({
      title: 'Send Password Reset?',
      text: `A password reset link will be sent to ${userEmail}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        setSendingReset(true);
        await sendPasswordResetEmail(auth, userEmail);
        
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Password reset email sent successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error: any) {
        console.error('Error sending password reset:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to send password reset email',
        });
      } finally {
        setSendingReset(false);
      }
    }
  };

  const displayUsers = searchQuery ? searchResults : users;

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <Users className="h-8 w-8" />
                User Management
              </h1>
              <p className="text-muted-foreground">Manage platform users and their details</p>
            </div>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Users
              </CardTitle>
              <CardDescription>Search by name or email address</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} disabled={searching}>
                  {searching ? 'Searching...' : 'Search'}
                </Button>
                {searchQuery && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Users List */}
          <Card>
            <CardHeader>
              <CardTitle>
                {searchQuery ? `Search Results (${displayUsers.length})` : 'Recent Users (Last 10 Sign-ups)'}
              </CardTitle>
              <CardDescription>
                {searchQuery ? 'Users matching your search criteria' : 'Most recently registered users'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {displayUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? 'No users found matching your search' : 'No users found'}
                </div>
              ) : (
                <div className="space-y-3">
                  {displayUsers.map((user) => (
                    <div
                      key={user.uid}
                      className="flex items-center justify-between rounded-lg border p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold">{user.name || 'Unnamed User'}</span>
                          {user.isAdmin && (
                            <Badge variant="destructive" className="text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                          {user.emailVerified ? (
                            <Badge variant="success" className="text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="warning" className="text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                          {user.twoFactorEnabled && (
                            <Badge variant="default" className="text-xs">
                              2FA
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Joined {format(user.createdAt, 'MMM d, yyyy')}
                          </span>
                          {user.units.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {user.units.length} unit{user.units.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewUser(user)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendPasswordReset(user.email)}
                          disabled={sendingReset}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Reset
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* View User Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>Complete information for this user</DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedUser.name || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">User ID</Label>
                  <p className="font-mono text-sm">{selectedUser.uid}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rank</Label>
                  <p className="font-medium">{selectedUser.rank || 'Not set'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Status</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-center gap-2">
                    {selectedUser.emailVerified ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span>Email {selectedUser.emailVerified ? 'Verified' : 'Not Verified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedUser.isAdmin ? (
                      <Shield className="h-4 w-4 text-red-600" />
                    ) : (
                      <User className="h-4 w-4 text-gray-600" />
                    )}
                    <span>{selectedUser.isAdmin ? 'Administrator' : 'Standard User'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedUser.twoFactorEnabled ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                    <span>2FA {selectedUser.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedUser.profileComplete ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-600" />
                    )}
                    <span>Profile {selectedUser.profileComplete ? 'Complete' : 'Incomplete'}</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Unit Memberships ({selectedUser.units.length})</h4>
                {selectedUser.units.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Not a member of any units</p>
                ) : (
                  <div className="space-y-2">
                    {userUnits.filter((u: any) => u.role !== 'Guest Subscription').map((unit: any) => (
                      <div key={unit.id} className="flex items-center justify-between p-3 border rounded">
                        <div className="flex-1">
                          <p className="font-medium">{unit.name} No. {unit.number}</p>
                          <p className="text-sm text-muted-foreground">{unit.type}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge>{unit.role}</Badge>
                          <span className="text-xs text-muted-foreground">
                            Joined {format(unit.joinedAt, 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Guest Subscriptions ({selectedUser.guestSubscriptions?.length || 0})</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Units this user is subscribed to for future event invitations
                </p>
                {(selectedUser.guestSubscriptions?.length || 0) === 0 ? (
                  <p className="text-muted-foreground text-sm">Not subscribed to any unit guest lists</p>
                ) : (
                  <div className="space-y-2">
                    {userUnits.filter((u: any) => u.role === 'Guest Subscription').map((unit: any) => (
                      <div key={unit.id} className="flex items-center justify-between p-3 border rounded bg-blue-50">
                        <div className="flex-1">
                          <p className="font-medium">{unit.name} No. {unit.number}</p>
                          <p className="text-sm text-muted-foreground">{unit.type}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Guest</Badge>
                          <span className="text-xs text-muted-foreground">
                            Subscribed {format(unit.joinedAt, 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Account Information</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="text-muted-foreground">Created</Label>
                    <p className="font-medium">{format(selectedUser.createdAt, 'PPpp')}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Last Login</Label>
                    <p className="font-medium">{format(selectedUser.lastLoginAt, 'PPpp')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
            {selectedUser && (
              <>
                <Button variant="outline" onClick={() => {
                  setIsViewDialogOpen(false);
                  handleEditUser(selectedUser);
                }}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit User
                </Button>
                <Button onClick={() => handleSendPasswordReset(selectedUser.email)}>
                  <Mail className="h-4 w-4 mr-1" />
                  Send Password Reset
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email (Read-only)</Label>
                <Input
                  id="edit-email"
                  value={editForm.email || ''}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label htmlFor="edit-rank">Rank</Label>
                <Input
                  id="edit-rank"
                  value={editForm.rank || ''}
                  onChange={(e) => setEditForm({ ...editForm, rank: e.target.value })}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Permissions & Status</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-admin"
                    checked={editForm.isAdmin || false}
                    onChange={(e) => setEditForm({ ...editForm, isAdmin: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit-admin" className="cursor-pointer">
                    Administrator privileges
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-verified"
                    checked={editForm.emailVerified || false}
                    onChange={(e) => setEditForm({ ...editForm, emailVerified: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit-verified" className="cursor-pointer">
                    Email verified
                  </Label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Note: Guest subscriptions are managed by users when they RSVP to events and opt-in to future invitations.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

